import { defineStore } from 'pinia';
import { useQueryClient } from '@tanstack/vue-query';
import { POSTHOG_EVENTS } from '@/constants/posthogEvents';
import { capturePosthogEvent } from '@/composables/usePosthog';
import { toast } from 'vue-sonner';
import { useStorage } from '@vueuse/core';
import { buildCapacityFlows } from './buildCapacityFlows';
import { getActionStyle } from '@/utilities/actionIcons';
import { useJobCountsStore } from '@store/jobCounts';
import { DIARY_COLORS } from '@/utilities/diaryColors';
import { newIdempotencyKey } from '@/utilities/idempotencyKey';

/**
 * Build an axios request config attaching the given (or freshly generated)
 * Idempotency-Key to the request headers. Callers pass one key per
 * user-initiated submit so retries within the submit reuse it while
 * distinct submits are treated independently by the backend middleware.
 */
function idempotentConfig(idempotencyKey) {
    return {
        headers: {
            'Idempotency-Key': idempotencyKey ?? newIdempotencyKey(),
        },
    };
}

export const useDiaryStore = defineStore('diary', () => {
    const queryClient = useQueryClient();
    const eventTravelMetricsRequests = new Map();

    // Capacity-flow echo state. Tracks subscribed tenant + the debounce timer
    // so subscribeToCapacityFlows() is idempotent across remounts and a burst
    // of broadcasts collapses into a single invalidation.
    let capacitySubscribedTenantId = null;
    let capacityDebounceTimer = null;

    function invalidateDiaries() {
        queryClient.invalidateQueries({ queryKey: ['/diaries'] });
        queryClient.invalidateQueries({ queryKey: ['/diaries/capacity'] });
        queryClient.invalidateQueries({ queryKey: ['/diaries/jobs'] });
        queryClient.invalidateQueries({ queryKey: ['/diaries/scheduled-dates'] });
        invalidateResourceAvailabilityQueries();
        invalidateConflictsBadge();
        invalidateMyTasks();
    }

    // Field-staff `me/tasks*` caches mirror diary state (assignments, acceptance
    // status), so refresh them with every diary refresh. Key prefixes are matched
    // element-wise, so the pending/calendar families need their own calls.
    function invalidateMyTasks() {
        queryClient.invalidateQueries({ queryKey: ['me/tasks'] });
        queryClient.invalidateQueries({ queryKey: ['me/tasks/pending'] });
        queryClient.invalidateQueries({ queryKey: ['me/tasks/accepted-on-behalf'] });
        queryClient.invalidateQueries({ queryKey: ['me/tasks/calendar'] });
    }

    /**
     * Subscribe to the tenant capacity channel and refetch diary state when
     * the API broadcasts `capacity-flows.changed`. Debounced to 250ms so a
     * multi-resource save (e.g. AssignTransport then AssignVessel on the same
     * event) coalesces into one refetch.
     *
     * Idempotent — calling again with the same tenantId is a no-op. Call from
     * the diary view's onMounted (or app boot once a tenantId is known).
     */
    function subscribeToCapacityFlows(tenantId) {
        if (!tenantId || capacitySubscribedTenantId === tenantId) {
            return;
        }

        if (capacitySubscribedTenantId !== null) {
            window.echo?.leave(`tenant.${capacitySubscribedTenantId}.capacity`);
        }
        capacitySubscribedTenantId = tenantId;

        window.echo?.private(`tenant.${tenantId}.capacity`)?.listen('.capacity-flows.changed', () => {
            if (capacityDebounceTimer) {
                clearTimeout(capacityDebounceTimer);
            }
            capacityDebounceTimer = setTimeout(() => {
                capacityDebounceTimer = null;
                queryClient.invalidateQueries({ queryKey: ['/diaries/capacity'] });
                invalidateResourceAvailabilityQueries();
            }, 250);
        });
    }

    /**
     * Invalidate per-event resource-availability query caches used by the
     * Assign{Transport,Container,Trailer}Form modals so that reopening a
     * sibling event's modal after an unassign sees the just-freed resource.
     *
     * Keys are namespaced as `<resource>-<status>-<eventId>` (see
     * AssignTransportForm.vue, AssignContainerForm.vue, AssignTrailerForm.vue
     * and SourceResourcePicker.vue), so a predicate matching the prefix is
     * the correct way to clear all event scopes at once.
     */
    function invalidateResourceAvailabilityQueries() {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const head = query.queryKey?.[0];
                if (typeof head !== 'string') return false;
                return (
                    head.startsWith('containers-available-') ||
                    head.startsWith('containers-unavailable-') ||
                    head.startsWith('transports-available-') ||
                    head.startsWith('transports-unavailable-') ||
                    head.startsWith('trailers-available-') ||
                    head.startsWith('trailers-unavailable-') ||
                    head.startsWith('source-picker-transports-available-') ||
                    head.startsWith('source-picker-transports-unavailable-')
                );
            },
        });
    }

    function invalidateConflictsBadge() {
        queryClient.invalidateQueries({ queryKey: ['fix-list-badge'] });
        queryClient.invalidateQueries({ queryKey: ['fix-list'] });
        useJobCountsStore().fetchConflictsCount();
    }

    /**
     * Patch a single event inside the cached diary infinite query pages.
     * Merges `updates` into the matching event object so the UI reflects
     * the change immediately without a full refetch.
     */
    function patchEventInDiaryCache(eventId, updates) {
        queryClient.setQueriesData({ queryKey: ['/diaries'] }, (oldData) => {
            if (!oldData?.pages) return oldData;

            return {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                    ...page,
                    data: (page.data || []).map((event) => (event.id === eventId ? { ...event, ...updates } : event)),
                })),
            };
        });
    }

    const schedules = ref([]);
    const schedulesLoading = ref(false);
    const pendings = ref([]);
    const pendingsLoading = ref(false);
    const pendingsLoadMoreLoading = ref(false);
    const isCollapsed = useStorage('diary-collapse-panel', false);
    const loading = ref(false);
    const depots = ref([]);
    const staff = ref([]);
    const events = ref([]);
    const capacity = ref({
        percentage: 0,
        total_space: 0,
        total_capacity: 0,
    });
    const transportUnavailabilities = ref([]);
    const selectedDiaryActions = ref([]);
    const actionTypes = ref([]);
    const runsheetReferences = ref([]);
    const connectedActions = ref([]);
    const selectedJobEventsLoading = ref(false);
    const selectedAddToRunsheetActions = ref([]);

    const previousSelectedDate = ref(null);
    const scheduleIndicators = ref([]);
    const searchedJobs = ref([]);
    const searchedJobsLoading = ref(false);
    const eventTravelMetrics = ref({});
    const eventTravelMetricsLoading = ref({});
    const savingEventIds = ref(new Set());

    async function fetchSchedules(params) {
        try {
            schedulesLoading.value = true;

            const response = await window.tenantAxios.get(`/diaries`, {
                params: params,
            });
            schedules.value = response.data;
        } finally {
            schedulesLoading.value = false;
        }
    }

    async function fetchPendings(payload, loadMore = false) {
        try {
            if (loadMore) {
                pendingsLoadMoreLoading.value = true;
            } else {
                pendingsLoading.value = true;
            }

            const params = {
                start: payload.start,
                end: payload.end,
                page: payload?.page || 1,
            };

            // Server-side search so filtering spans the whole paginated result
            // set, not just the page already loaded on the client.
            if (payload?.search) {
                params.search = payload.search;
            }

            if (payload?.include_services != null) {
                params.include_services = payload.include_services ? '1' : '0';
            } else {
                params.include_services = '1';
            }

            if (payload?.depot) {
                params.depot = payload.depot;
            }

            const response = await window.tenantAxios.get(`/diaries/jobs`, { params });

            // If it's page 1 or no existing data, replace the entire array
            if (!payload?.page || payload.page === 1 || !pendings.value?.data) {
                pendings.value = response.data;
            } else {
                // If it's a subsequent page, append the new data
                pendings.value = {
                    ...response.data,
                    data: [...(pendings.value.data || []), ...response.data.data],
                };
            }
        } finally {
            pendingsLoading.value = false;
            pendingsLoadMoreLoading.value = false;
        }
    }

    async function postSchedule(data, { idempotencyKey } = {}) {
        try {
            loading.value = true;
            const resp = await window.tenantAxios.post(`/diaries`, data, idempotentConfig(idempotencyKey));
            capturePosthogEvent(POSTHOG_EVENTS.SCHEDULE_TASK_CREATED);

            // Invalidate diaries query to refetch with current URL params
            // This ensures the diaries table is refreshed after creating a new schedule
            invalidateDiaries();

            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    async function updateSchedule(schedule_id, payload, { idempotencyKey } = {}) {
        try {
            loading.value = true;
            const resp = await window.tenantAxios.patch(
                `/diaries/${schedule_id}`,
                payload,
                idempotentConfig(idempotencyKey),
            );

            // Invalidate diaries query to refetch with current URL params
            // This ensures the diaries table is refreshed after updating a schedule
            invalidateDiaries();

            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    // In-flight dedup for storeEvent keyed on the submit's Idempotency-Key.
    // Prevents two spinners on a double-click without risking silent payload
    // collapse: only calls sharing the exact same key (same user submit)
    // coalesce. Distinct submits with different keys each run separately.
    // The backend EnforceIdempotency middleware is the source of truth for
    // correctness — this Map is just a UX optimisation.
    const inFlightEventStores = new Map();

    async function storeEvent(item, payload, { idempotencyKey } = {}) {
        const schedule_id = item.schedule_id;
        const service_id = item.schedule_service_id;
        const key = idempotencyKey ?? newIdempotencyKey();

        if (inFlightEventStores.has(key)) {
            return inFlightEventStores.get(key);
        }

        loading.value = true;
        const request = window.tenantAxios
            .post(`/diaries/${schedule_id}/services/${service_id}/events`, payload, idempotentConfig(key))
            .then((resp) => {
                capturePosthogEvent(POSTHOG_EVENTS.SCHEDULE_TASK_CREATED);

                // Invalidate diaries query to refetch with current URL params
                // This ensures the diaries table is refreshed after creating a new event
                invalidateDiaries();

                return resp.data;
            })
            .finally(() => {
                inFlightEventStores.delete(key);
                loading.value = false;
            });

        inFlightEventStores.set(key, request);

        return request;
    }

    async function updateEvent(item, payload, { skipFullInvalidation = false, idempotencyKey } = {}) {
        loading.value = true;
        const event_id = item.id;
        const schedule_id = item.schedule_id;
        const service_id = item.schedule_service_id;

        // schedule_id / schedule_service_id are whenLoaded('scheduleService')
        // fields on DiaryEventResource. If an endpoint feeding this item ever
        // stops eager-loading scheduleService they arrive undefined, which would
        // otherwise build a `/diaries/undefined/...` PATCH → 404. Fail loud.
        if (!schedule_id || !service_id) {
            toast.error('Cannot update task: missing schedule details. Please refresh and try again.');
            loading.value = false;
            return;
        }

        savingEventIds.value = new Set([...savingEventIds.value, event_id]);

        try {
            const resp = await window.tenantAxios.patch(
                `/diaries/${schedule_id}/services/${service_id}/events/${event_id}`,
                payload,
                idempotentConfig(idempotencyKey),
            );

            if (skipFullInvalidation) {
                // Only refresh the conflicts badge; the caller is responsible
                // for patching the diary cache with the known new state.
                invalidateConflictsBadge();
            } else {
                // Optimistically patch this event in cache from the response data
                // so the UI updates immediately, then background-refresh all diaries.
                const updatedEvent =
                    resp.data?.schedule_service_event?.data ??
                    resp.data?.schedule_service_event ??
                    resp.data?.data ??
                    resp.data;
                if (updatedEvent?.id) {
                    patchEventInDiaryCache(updatedEvent.id, updatedEvent);
                }
                invalidateDiaries();
            }

            return resp.data;
        } catch (error) {
            // Handle 422 validation errors with toast notification
            // Note: axios interceptor processes errors, so we check error.code instead of error.response.status
            if (error?.code === 422 || error?.response?.status === 422) {
                const errorMessage = error?.message || error?.response?.data?.message;
                const errorErrors = error?.errors || error?.response?.data?.errors;

                let formattedMessages = '';

                // Add main message without bullet point
                if (errorMessage) {
                    formattedMessages = errorMessage;
                }

                // Add additional error messages with bullet points
                if (errorErrors) {
                    const additionalMessages = [];
                    Object.values(errorErrors).forEach((msgs) => {
                        const errorArray = Array.isArray(msgs) ? msgs : [msgs];
                        errorArray.forEach((msg) => {
                            // Only add if it's different from the main message
                            if (msg !== errorMessage) {
                                additionalMessages.push(`• ${msg}`);
                            }
                        });
                    });

                    if (additionalMessages.length > 0) {
                        formattedMessages =
                            formattedMessages ?
                                `${formattedMessages}\n${additionalMessages.join('\n')}`
                            :   additionalMessages.join('\n');
                    }
                }

                if (formattedMessages) {
                    toast.error(formattedMessages, {
                        style: { whiteSpace: 'pre-line' },
                    });
                } else {
                    toast.error('Cannot update event: Validation error');
                }
            }
            // Re-throw the error so calling functions can handle it if needed
            throw error;
        } finally {
            loading.value = false;
            // Keep spinner visible long enough for the user to see feedback after modal closes.
            // The modal takes ~200ms to close after the API completes, so 1000ms ensures
            // at least ~800ms of visible spinner in the table.
            setTimeout(() => {
                const next = new Set(savingEventIds.value);
                next.delete(event_id);
                savingEventIds.value = next;
            }, 1000);
        }
    }

    async function detectTimezone(schedule_id, service_id, event_id, payload) {
        try {
            const resp = await window.tenantAxios.post(
                `/diaries/${schedule_id}/services/${service_id}/events/${event_id}/detect-timezone`,
                payload,
                idempotentConfig(),
            );

            // Invalidate diaries query to refetch with current URL params
            // This ensures the diaries table is refreshed after timezone detection
            invalidateDiaries();

            return resp.data;
        } catch (error) {
            console.error('Error detecting timezone:', error);
            throw error;
        }
    }

    async function checkChangeConflicts(schedule_id, service_id, event_id, payload) {
        try {
            const resp = await window.tenantAxios.post(
                `/diaries/${schedule_id}/services/${service_id}/events/${event_id}/change-conflicts`,
                payload,
                idempotentConfig(),
            );

            return resp.data;
        } catch (error) {
            console.error('Error checking change conflicts:', error);
            throw error;
        }
    }

    async function assignStaff(item, payload, newUsers = [], { idempotencyKey } = {}) {
        loading.value = true;

        const event_id = item.id;
        const schedule_id = item.schedule_id;
        const service_id = item.schedule_service_id;

        savingEventIds.value = new Set([...savingEventIds.value, event_id]);

        try {
            const body = {
                assigned_staff: payload,
            };

            if (newUsers.length > 0) {
                body.new_users = newUsers;
            }

            const resp = await window.tenantAxios.post(
                `/schedules/${schedule_id}/services/${service_id}/events/${event_id}/assign-multi-staff`,
                body,
                idempotentConfig(idempotencyKey),
            );

            // Invalidate diaries query to refetch with current URL params
            // This ensures the diaries table is refreshed after assigning staff
            invalidateDiaries();

            return resp.data;
        } finally {
            loading.value = false;
            setTimeout(() => {
                const next = new Set(savingEventIds.value);
                next.delete(event_id);
                savingEventIds.value = next;
            }, 1000);
        }
    }

    /**
     * Per-staff-assignment accept/decline mutations (CU-86d2dcc7y).
     *
     * These mirror the `assignStaff` shape: post to the tenant-scoped nested route, call
     * `invalidateDiaries()` to refresh the diary table and fix-list cache, and return the
     * server payload so callers can patch optimistically if they want a faster UI response.
     */
    async function acceptAssignment(item, staff_id, { idempotencyKey } = {}) {
        loading.value = true;

        try {
            const resp = await window.tenantAxios.post(
                `/schedules/${item.schedule_id}/services/${item.schedule_service_id}/events/${item.id}/staff/${staff_id}/accept`,
                {},
                idempotentConfig(idempotencyKey),
            );
            invalidateDiaries();
            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    async function declineAssignment(item, staff_id, decline_reason = null, { idempotencyKey } = {}) {
        loading.value = true;

        try {
            const resp = await window.tenantAxios.post(
                `/schedules/${item.schedule_id}/services/${item.schedule_service_id}/events/${item.id}/staff/${staff_id}/decline`,
                decline_reason ? { decline_reason } : {},
                idempotentConfig(idempotencyKey),
            );
            invalidateDiaries();
            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Dismiss a task the office accepted on the staff member's behalf. Clears the
     * personal notification flag only — `acceptance_status` stays `accepted` and the
     * route is not lock-guarded. Mirrors `acceptAssignment` otherwise.
     */
    async function dismissAssignment(item, staff_id, { idempotencyKey } = {}) {
        loading.value = true;

        try {
            const resp = await window.tenantAxios.post(
                `/schedules/${item.schedule_id}/services/${item.schedule_service_id}/events/${item.id}/staff/${staff_id}/dismiss`,
                {},
                idempotentConfig(idempotencyKey),
            );
            invalidateDiaries();
            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    async function addStaff(item, user_id, { idempotencyKey } = {}) {
        try {
            loading.value = true;

            const resp = await window.tenantAxios.post(
                `/schedules/${item.schedule_id}/staff`,
                { user_id },
                idempotentConfig(idempotencyKey),
            );

            // Invalidate diaries query to refetch with current URL params
            // This ensures the diaries table is refreshed after adding staff
            invalidateDiaries();

            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    async function getDepots() {
        try {
            loading.value = true;
            const response = await window.tenantAxios.get(`/diaries/depots`);
            depots.value = response.data;
            return response.data;
        } finally {
            loading.value = false;
        }
    }

    async function getStaff() {
        const response = await window.tenantAxios.get(`/users?roles[]=FIELD_STAFF&roles[]=CONTRACTOR`);
        // Paginated users index returns `{ data: [...] }`; never leave a non-array
        // in `staff` — diary filter options call `.map` on it.
        staff.value =
            Array.isArray(response.data?.data) ? response.data.data
            : Array.isArray(response.data) ? response.data
            : [];
        return response.data;
    }

    async function getCapacity(payload) {
        const response = await window.tenantAxios.get(`/diaries/capacity`, {
            params: payload,
        });
        capacity.value = response.data;
        return response.data;
    }

    async function lockDate(selectedDate, locked, depotId = null, { idempotencyKey } = {}) {
        try {
            loading.value = true;
            const payload = {
                date: selectedDate,
                locked: locked,
            };

            // Add depot_id to payload if provided
            if (depotId) {
                payload.depot_id = depotId;
            }

            const response = await window.tenantAxios.post(
                `/diaries/locked-dates`,
                payload,
                idempotentConfig(idempotencyKey),
            );
            return response.data;
        } finally {
            loading.value = false;
        }
    }

    async function getLockedDateStatus(selectedDate) {
        const response = await window.tenantAxios.get(`/diaries/locked-dates/by-date`, {
            params: { date: selectedDate },
        });
        return response.data;
    }

    async function checkDateLock(startDate, endDate, depotId = null, serviceableType = null, serviceableId = null) {
        try {
            // Only make the API call if serviceableType is 'move' or if no serviceableType is provided (for backward compatibility)
            if (serviceableType && serviceableType !== 'move') {
                // Return default response for non-move serviceable types
                return { is_locked: false };
            }

            const payload = {
                start_date: startDate,
                end_date: endDate,
            };

            // Add depot_id to payload if provided
            if (depotId) {
                payload.depot_id = depotId;
            }

            // Add job information if provided
            if (serviceableType) {
                payload.serviceable_type = serviceableType;
            }
            if (serviceableId) {
                payload.serviceable_id = serviceableId;
            }

            const response = await window.tenantAxios.post(`/diaries/check-date`, payload, idempotentConfig());
            return response.data;
        } catch (error) {
            console.error('Error checking date lock:', error);
            throw error;
        }
    }

    async function getTransportUnavailabilities(payload) {
        const response = await window.tenantAxios.get(`/diaries/transport-unavailabilities`, {
            params: {
                start_date: payload.start_datetime,
                end_date: payload.end_datetime,
            },
        });
        transportUnavailabilities.value = response.data.unavailabilities;
        return response.data;
    }

    async function getActionTypes(serviceableType = null) {
        try {
            // Normalize serviceable_type to lowercase for consistent API handling
            const normalizedType = serviceableType ? serviceableType.toLowerCase() : null;
            const params = normalizedType ? { serviceable_type: normalizedType } : {};
            const response = await window.tenantAxios.get(`/diaries/action-types`, { params });
            actionTypes.value = response.data;
            return response.data;
        } catch (error) {
            console.error('Error fetching action types:', error);
            // Return empty array on error to prevent breaking the UI
            actionTypes.value = [];
            throw error;
        }
    }

    async function createRun(payload, { idempotencyKey } = {}) {
        const response = await window.tenantAxios.post(
            `/diaries/create-run`,
            payload,
            idempotentConfig(idempotencyKey),
        );

        // Invalidate diaries query to refetch with current URL params
        // This ensures the diaries table is refreshed after creating a run
        invalidateDiaries();

        return response.data;
    }

    async function addToRun(selectedActions, scheduleId, { idempotencyKey } = {}) {
        const response = await window.tenantAxios.patch(
            `/diaries/update-run/${scheduleId}`,
            { selected_actions: selectedActions },
            idempotentConfig(idempotencyKey),
        );

        // Invalidate diaries query to refetch with current URL params
        // This ensures the diaries table is refreshed after adding actions to a run
        invalidateDiaries();

        return response.data;
    }

    /**
     * Normalize list API payloads to a plain array.
     * The diaries/runsheet-references endpoint returns a raw JSON array, but
     * callers must tolerate envelopes (`{ data: [...] }`) and unexpected shapes —
     * assigning a non-array makes `runsheetReferences.map` throw in the diary UI
     * (MOVEPRO-CRM-KA).
     */
    function normalizeListPayload(payload) {
        if (Array.isArray(payload)) {
            return payload;
        }
        if (Array.isArray(payload?.data)) {
            return payload.data;
        }
        return [];
    }

    function isCancelOrNetworkNoise(error) {
        return (
            error == null ||
            error?.code === 'ERR_CANCELED' ||
            error?.code === 'ERR_NETWORK' ||
            error?.name === 'CanceledError' ||
            error?.message === 'Network Error'
        );
    }

    async function getRunsheetReferences() {
        try {
            const response = await window.tenantAxios.get(`/diaries/runsheet-references`);
            runsheetReferences.value = normalizeListPayload(response.data);
            return runsheetReferences.value;
        } catch (error) {
            // Route aborts (status_code: 0) reject through the axios interceptor as
            // undefined / CanceledError. Keep the last good list and do not throw
            // through diary onMounted / filter refresh paths.
            if (isCancelOrNetworkNoise(error)) {
                if (!Array.isArray(runsheetReferences.value)) {
                    runsheetReferences.value = [];
                }
                return runsheetReferences.value;
            }
            if (!Array.isArray(runsheetReferences.value)) {
                runsheetReferences.value = [];
            }
            throw error;
        }
    }

    async function unscheduleEvent(item, { idempotencyKey } = {}) {
        const resp = await window.tenantAxios.delete(
            `/diaries/${item.schedule_id}/services/${item.schedule_service_id}/events/${item.id}`,
            idempotentConfig(idempotencyKey),
        );

        // Invalidate diaries query to refetch with current URL params
        // This ensures the diaries table is refreshed after unscheduling an event
        invalidateDiaries();

        return resp.data;
    }

    async function unlinkScheduleService(item, { idempotencyKey } = {}) {
        const response = await window.tenantAxios.post(
            `/diaries/unlink-schedule-service/${item.id}`,
            {},
            idempotentConfig(idempotencyKey),
        );

        // Invalidate diaries query to refetch with current URL params
        // This ensures the diaries table is refreshed after unlinking a schedule service
        invalidateDiaries();

        return response.data;
    }

    async function getConnectedActions(item) {
        const response = await window.tenantAxios.get(`/diaries/connected-actions/${item.schedule_service_id}`);
        connectedActions.value = response.data.connected_actions;
        return response.data;
    }

    async function getScheduleServices(scheduleId, serviceType) {
        try {
            loading.value = true;
            const response = await window.tenantAxios.get(`/schedules/${scheduleId}/services`, {
                params: { service_type: serviceType },
            });
            return response.data;
        } finally {
            loading.value = false;
        }
    }

    async function updateActionsColor(eventIds, color, { idempotencyKey } = {}) {
        const response = await window.tenantAxios.patch(
            '/diaries/actions/color',
            { event_ids: eventIds, color: color },
            idempotentConfig(idempotencyKey),
        );

        // Optimistically update local schedules so calendar views reflect the change immediately
        const colorHex = color ? (DIARY_COLORS[color] ?? null) : null;
        if (schedules.value?.data) {
            schedules.value = {
                ...schedules.value,
                data: schedules.value.data.map((s) => {
                    if (eventIds.includes(s.id)) {
                        return { ...s, color, color_hex: colorHex };
                    }
                    return s;
                }),
            };
        }

        queryClient.invalidateQueries({
            queryKey: ['/diaries'],
        });

        return response.data;
    }

    async function deleteActions(actionIds, { idempotencyKey } = {}) {
        const response = await window.tenantAxios.delete(`/diaries/actions`, {
            data: { event_ids: actionIds },
            headers: { 'Idempotency-Key': idempotencyKey ?? newIdempotencyKey() },
        });

        // Invalidate diaries query to refetch with current URL params
        // This ensures the diaries table is refreshed after deleting actions
        invalidateDiaries();

        return response.data;
    }

    async function unassignTransport(item) {
        try {
            loading.value = true;
            const payload = {
                origin: 'diary',
                transport_id: null,
            };
            await updateEvent(item, payload);
        } finally {
            loading.value = false;
        }
    }

    async function unassignStaff(item) {
        try {
            loading.value = true;
            await assignStaff(item, []);
        } finally {
            loading.value = false;
        }
    }

    async function unassignContainers(item) {
        try {
            loading.value = true;
            const payload = {
                origin: 'diary',
                vessels: [],
                vessel_type: 'CONTAINER',
            };
            await updateEvent(item, payload);
        } finally {
            loading.value = false;
        }
    }

    async function unassignTrailers(item) {
        try {
            loading.value = true;
            const payload = {
                origin: 'diary',
                vessels: [],
                vessel_type: 'TRAILER',
            };
            await updateEvent(item, payload);
        } finally {
            loading.value = false;
        }
    }

    async function quickRemoveTransport(item, transportId, role) {
        try {
            loading.value = true;

            // Save original state for rollback on error
            const previousState = {
                transports: item.transports,
                capacity_flows: item.capacity_flows,
            };

            const isSourceOnly = role === 'source' && !(item.transports || []).some((t) => t.id === transportId);

            if (isSourceOnly) {
                const remaining = (item.transports || []).map((t) => ({
                    id: t.id,
                    allocated_capacity: t.allocated_capacity,
                }));

                const updatedFlows = (item.capacity_flows || [])
                    .filter((f) => {
                        if (f.source_type === 'transport' && f.source_id === transportId) {
                            return false;
                        }
                        return true;
                    })
                    .map((f) => ({
                        source_type: f.source_type,
                        source_id: f.source_id,
                        destination_type: f.destination_type,
                        destination_id: f.destination_id,
                        capacity: f.capacity,
                    }));

                const payload = {
                    origin: 'diary',
                    transports: remaining,
                    capacity_flows: updatedFlows,
                };

                // Optimistically update the cache: remove flows referencing this transport as source
                patchEventInDiaryCache(item.id, {
                    capacity_flows: (item.capacity_flows || []).filter(
                        (f) => !(f.source_type === 'transport' && f.source_id === transportId),
                    ),
                });

                try {
                    await updateEvent(item, payload, { skipFullInvalidation: false });
                } catch (error) {
                    patchEventInDiaryCache(item.id, previousState);
                    throw error;
                }
            } else {
                const remaining = (item.transports || [])
                    .filter((t) => t.id !== transportId)
                    .map((t) => ({
                        id: t.id,
                        allocated_capacity: t.allocated_capacity,
                    }));

                const payload = {
                    origin: 'diary',
                    transports: remaining,
                    capacity_flows: buildCapacityFlows(item, remaining, 'transport', transportId),
                };

                // Optimistically update the cache: remove the transport and its capacity flows
                patchEventInDiaryCache(item.id, {
                    transports: (item.transports || []).filter((t) => t.id !== transportId),
                    capacity_flows: (item.capacity_flows || []).filter(
                        (f) =>
                            !(f.source_type === 'transport' && f.source_id === transportId) &&
                            !(f.destination_type === 'transport' && f.destination_id === transportId),
                    ),
                });

                try {
                    await updateEvent(item, payload, { skipFullInvalidation: false });
                } catch (error) {
                    patchEventInDiaryCache(item.id, previousState);
                    throw error;
                }
            }
        } finally {
            loading.value = false;
        }
    }

    async function quickRemoveVessel(item, vesselId, vesselType, role) {
        try {
            loading.value = true;

            // Save original state for rollback on error
            const previousState = {
                vessels: item.vessels,
                capacity_flows: item.capacity_flows,
            };

            const allVesselsOfType = (item.vessels || []).filter(
                (v) => v.type === vesselType || v.vessel_type === vesselType,
            );

            const isSourceOnly = role === 'source' && !allVesselsOfType.some((v) => v.id === vesselId);

            if (isSourceOnly) {
                const remainingVessels = allVesselsOfType.map((v) => ({
                    id: v.id,
                    type: vesselType,
                    allocation: v.allocated_capacity,
                }));

                const updatedFlows = (item.capacity_flows || [])
                    .filter((f) => {
                        if (f.source_type === 'vessel' && f.source_id === vesselId) {
                            return false;
                        }
                        return true;
                    })
                    .map((f) => ({
                        source_type: f.source_type,
                        source_id: f.source_id,
                        destination_type: f.destination_type,
                        destination_id: f.destination_id,
                        capacity: f.capacity,
                    }));

                const payload = {
                    origin: 'diary',
                    vessels: remainingVessels,
                    capacity_flows: updatedFlows,
                };

                // Optimistically update the cache: remove flows referencing this vessel as source
                patchEventInDiaryCache(item.id, {
                    capacity_flows: (item.capacity_flows || []).filter(
                        (f) => !(f.source_type === 'vessel' && f.source_id === vesselId),
                    ),
                });

                try {
                    await updateEvent(item, payload, { skipFullInvalidation: false });
                } catch (error) {
                    patchEventInDiaryCache(item.id, previousState);
                    throw error;
                }
            } else {
                const remaining = allVesselsOfType
                    .filter((v) => v.id !== vesselId)
                    .map((v) => ({
                        id: v.id,
                        type: vesselType,
                        allocation: v.allocated_capacity,
                    }));

                const payload = {
                    origin: 'diary',
                    vessels: remaining,
                    capacity_flows: buildCapacityFlows(item, remaining, 'vessel', vesselId),
                };

                if (remaining.length === 0) {
                    payload.vessel_type = vesselType;
                }

                // Optimistically update the cache: remove the vessel and its capacity flows
                patchEventInDiaryCache(item.id, {
                    vessels: (item.vessels || []).filter((v) => v.id !== vesselId),
                    capacity_flows: (item.capacity_flows || []).filter(
                        (f) =>
                            !(f.source_type === 'vessel' && f.source_id === vesselId) &&
                            !(f.destination_type === 'vessel' && f.destination_id === vesselId),
                    ),
                });

                try {
                    await updateEvent(item, payload, { skipFullInvalidation: false });
                } catch (error) {
                    patchEventInDiaryCache(item.id, previousState);
                    throw error;
                }
            }
        } finally {
            loading.value = false;
        }
    }

    async function removeDownstreamResources(item, resourceType, vesselType, { idempotencyKey } = {}) {
        try {
            loading.value = true;
            const schedule_id = item.schedule_id;
            const service_id = item.schedule_service_id;
            const event_id = item.id;

            const payload = { resource_type: resourceType };
            if (vesselType) {
                payload.vessel_type = vesselType;
            }

            const resp = await window.tenantAxios.post(
                `/diaries/${schedule_id}/services/${service_id}/events/${event_id}/remove-downstream-resources`,
                payload,
                idempotentConfig(idempotencyKey),
            );

            invalidateDiaries();

            return resp.data;
        } finally {
            loading.value = false;
        }
    }

    async function getAllServices(payload) {
        const response = await window.tenantAxios.get(`/diaries/services`, {
            params: payload,
        });
        return response.data;
    }

    async function getAllServiceEvents(serviceId) {
        try {
            selectedJobEventsLoading.value = true;
            const response = await window.tenantAxios.get(`/diaries/service/${serviceId}/events`);
            return response.data;
        } finally {
            selectedJobEventsLoading.value = false;
        }
    }

    function getEventTravelMetricsKey(item, startAddressId, endAddressId) {
        if (!item?.id || !item?.schedule_id || !item?.schedule_service_id || !startAddressId || !endAddressId) {
            return null;
        }

        return [item.id, item.schedule_id, item.schedule_service_id, startAddressId, endAddressId].join(':');
    }

    async function getEventTravelMetrics(item, startAddressId, endAddressId) {
        const cacheKey = getEventTravelMetricsKey(item, startAddressId, endAddressId);

        if (!cacheKey) {
            return null;
        }

        const cached = eventTravelMetrics.value[cacheKey];
        if (cached) {
            // Cache terminal failures per key so a persistent 4xx/5xx stops
            // a hover-driven retry loop. The cached error clears when the
            // address pair changes (cacheKey encodes both IDs).
            if (cached.__error) {
                throw new Error(cached.message);
            }
            return cached;
        }

        if (eventTravelMetricsRequests.has(cacheKey)) {
            return eventTravelMetricsRequests.get(cacheKey);
        }

        const request = (async () => {
            try {
                eventTravelMetricsLoading.value = {
                    ...eventTravelMetricsLoading.value,
                    [cacheKey]: true,
                };

                const response = await window.tenantAxios.get(
                    `/diaries/${item.schedule_id}/services/${item.schedule_service_id}/events/${item.id}/travel-metrics`,
                );

                eventTravelMetrics.value = {
                    ...eventTravelMetrics.value,
                    [cacheKey]: response.data,
                };

                return response.data;
            } catch (error) {
                const message = error?.response?.data?.message || error?.message || 'Travel metrics unavailable';
                eventTravelMetrics.value = {
                    ...eventTravelMetrics.value,
                    [cacheKey]: { __error: true, message },
                };
                throw error;
            } finally {
                eventTravelMetricsLoading.value = {
                    ...eventTravelMetricsLoading.value,
                    [cacheKey]: false,
                };
                eventTravelMetricsRequests.delete(cacheKey);
            }
        })();

        eventTravelMetricsRequests.set(cacheKey, request);

        return request;
    }

    async function getScheduleIndicators(params) {
        try {
            schedulesLoading.value = true;

            const response = await window.tenantAxios.get(`/diaries/scheduled-dates`, {
                params: params,
            });
            scheduleIndicators.value = response.data;
        } catch (error) {
            // Ignore aborted requests
            if (error?.code === 'ERR_CANCELED' || error?.message?.includes('canceled')) {
                return;
            }
            // Re-throw error so component can handle it
            throw error;
        } finally {
            schedulesLoading.value = false;
        }
    }

    function getAction(action) {
        return getActionStyle(action);
    }

    async function getStorageAvailability(eventId, depotId = null) {
        const params = { event_id: eventId };
        if (depotId) {
            params.depot_id = depotId;
        }
        const response = await window.tenantAxios.get(`/diaries/storage-availability`, { params });
        return response.data;
    }

    async function searchAllJobs(payload) {
        try {
            searchedJobsLoading.value = true;
            const params = {
                search: payload.search,
                include_services: '1',
                paginate: false,
                skip_filters: true,
            };

            if (payload?.depot) {
                params.depot = payload.depot;
            }

            const response = await window.tenantAxios.get(`/diaries/jobs`, { params });
            searchedJobs.value = response.data.data;
        } finally {
            searchedJobsLoading.value = false;
        }
    }

    return {
        schedules,
        schedulesLoading,
        pendings,
        isCollapsed,
        pendingsLoading,
        pendingsLoadMoreLoading,
        loading,
        depots,
        staff,
        events,
        capacity,
        transportUnavailabilities,
        selectedDiaryActions,
        actionTypes,
        runsheetReferences,
        connectedActions,
        selectedJobEventsLoading,
        selectedAddToRunsheetActions,
        previousSelectedDate,
        scheduleIndicators,
        searchedJobs,
        searchedJobsLoading,
        eventTravelMetrics,
        eventTravelMetricsLoading,
        savingEventIds,

        // Actions
        getAction,
        fetchSchedules,
        fetchPendings,
        postSchedule,
        getDepots,
        updateSchedule,
        updateEvent,
        detectTimezone,
        checkChangeConflicts,
        getStaff,
        assignStaff,
        acceptAssignment,
        declineAssignment,
        dismissAssignment,
        storeEvent,
        addStaff,
        getCapacity,
        lockDate,
        getLockedDateStatus,
        checkDateLock,
        getTransportUnavailabilities,
        getActionTypes,
        createRun,
        addToRun,
        getRunsheetReferences,
        unscheduleEvent,
        unlinkScheduleService,
        getConnectedActions,
        getScheduleServices,
        updateActionsColor,
        deleteActions,
        unassignTransport,
        unassignStaff,
        unassignContainers,
        unassignTrailers,
        quickRemoveTransport,
        quickRemoveVessel,
        removeDownstreamResources,
        patchEventInDiaryCache,
        invalidateDiaries,
        invalidateMyTasks,
        subscribeToCapacityFlows,
        getAllServices,
        getAllServiceEvents,
        getEventTravelMetricsKey,
        getEventTravelMetrics,
        getScheduleIndicators,
        getStorageAvailability,
        searchAllJobs,
    };
});
